import { useSpring } from "react-spring";
import { colors, screenHues, screens } from "../constants/constants";
import { springConfigs } from "../constants/springConfigs";
import { useMargins } from "../hooks/hooks";
import { useWindowSize } from "../hooks/useWindowSize";
import { OnCurtainStart } from "../types/types";
import {
  Arrows,
  LeftArrow,
  RightArrow,
} from "./ScreenLanding/components/Arrows";
import { Contour } from "./ScreenLanding/components/Contour";
import { IocoText } from "./ScreenLanding/components/IocoText";
import { PageNumber } from "./ScreenLanding/components/PageNumber";
import { SVGCircle } from "./ScreenMenu/components/SVGCircle";
import { Hamburger } from "./shared/Hamburger";
import { RectangleButton } from "./shared/RectangleButton";
import { ScreenBackground } from "./shared/ScreenBackground";
import { AnimatedPerf } from "./shared/SVGComponents";
import { Video } from "./shared/Video";

const Biocomputer = () => {
  const { h } = useWindowSize();

  return (
    <div
      {...{
        id: "row3-BIOCOMPUTER",
        style: {
          gridArea: "d",
          placeSelf: "center",
          color: colors.pink,
          fontSize: h(1.3),
          letterSpacing: "4px",
          fontWeight: 600,
          display: "flex",
          justifyContent: "center",
        },
      }}
    >
      BIOCOMPUTER
    </div>
  );
};

const IOCO = () => {
  const { h } = useWindowSize();
  return (
    <div
      {...{
        id: "IOCO",
        style: {
          gridArea: "c",
          fontSize: h(14),
          lineHeight: 0,
          fontWeight: 500,
          letterSpacing: "-2px",
          placeSelf: "center",
        },
      }}
    >
      IOCO
    </div>
  );
};

const BetterThanQuantum = () => {
  const betterThanQuantum = " BETTER THAN QUANTUM ";

  return (
    <div
      {...{
        id: "betterThanQuantum",
        style: {
          gridArea: "b",
          fontSize: "0.5rem",
          display: "flex",
          justifyContent: "space-between",
        },
      }}
    >
      <>
        {betterThanQuantum.split("").map((character, i) => (
          <div
            {...{
              id: "",
              style: {},
              key: i,
            }}
          >
            {character}
          </div>
        ))}
      </>
    </div>
  );
};

export const ScreenPerformance = ({
  isCurtainActive,
  isCurtainIntro,
  onCurtainStart,
}: {
  isCurtainActive: boolean;
  isCurtainIntro: boolean;
  onCurtainStart: OnCurtainStart;
}) => {
  const background = `
  radial-gradient(circle at 50% 0%, #703CAF55 0, transparent 50%),
  radial-gradient(circle at 50% 0%, #3A2255BB 0, transparent 90%),
  linear-gradient(#2A225A, #190E29)
  `;

  const isContentShown =
    !isCurtainActive || (isCurtainActive && isCurtainIntro);

  const { liftAsset } = useSpring({
    from: { liftAsset: "-50%" },
    to: { liftAsset: "-40%" },
    config: {
      tension: 180,
      friction: 120,
      mass: 10,
      precision: 0.00001,
    },
  });

  const { opacity } = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: springConfigs.assetAppears,
  });

  const { w, h, horizontal, vertical } = {
    ...useWindowSize(),
  };
  const { screenContentVerticalPadding, screenContentHorizontalPadding } =
    useMargins();

  // const whiteLineMargin = `${(appContentContainerWidthValue * 0.13).toFixed(
  //   2
  // )}${appContentContainerWidthUnit}`;

  // console.log(`-2px ${whiteLineMargin} ${whiteLineMargin} ${whiteLineMargin}`);

  return (
    <>
      <ScreenBackground
        {...{
          background,
          screenName: screens.preorder,
          gridArea: "AppContentContainer",
        }}
      />

      <div
        id="white-line"
        className=""
        {...{
          style: {
            gridArea: "AppContentContainer",
            margin: "-20% auto auto auto",
            width: horizontal ? "80%" : "100%",
            height: horizontal ? "110%" : "50%",
            // margin: "auto",
            backgroundColor: "#ffffff02",
            borderRadius: "0 0 9999px 9999px",
            border: "1px solid rgba(256,256,256,.05)",
          },
        }}
      ></div>

      <Contour {...{ gridArea: "AppContentContainer" }} />

      {/* <Lines {...{ gridArea: "AppContentContainer" }} /> */}

      {isContentShown && (
        <>
          <Video
            {...{
              containerStyle: {
                gridArea: "AppContentContainer",
                width: horizontal ? "55%" : "90%",
                placeSelf: "start center",
              },
              videoStyle: {
                marginTop: liftAsset,
                opacity,
                transform: "scaleX(-1) rotate(270deg)",
                filter: screenHues.performance,
              },
            }}
          />
          <div
            id="contentGrid"
            className=""
            {...{
              style: {
                gridArea: "AppContentContainer",
                display: "grid",
                padding: `${screenContentVerticalPadding} ${screenContentHorizontalPadding}`, // start with first line
                gridTemplateColumns: `repeat(${
                  horizontal ? 7 : 4
                }, minmax(0, 1fr))`, // https://stackoverflow.com/questions/43311943/prevent-content-from-expanding-grid-items
                gridTemplateRows: "repeat(11, minmax(0, 1fr))", // https://stackoverflow.com/questions/43311943/prevent-content-from-expanding-grid-items
              },
            }}
          >
            <IocoText
              {...{
                onCurtainStart,
                gridArea: horizontal ? "1/1" : "1/1", // second row first colum
              }}
            />
            <Hamburger
              {...{
                onCurtainStart,
                gridArea: horizontal ? "1/7/1/8" : "1/-2/2/-1",
              }}
            />
            {horizontal && (
              <Arrows
                {...{
                  gridArea: "5/1/7/2",
                  onCurtainStart,
                  screenLeft: "product",
                  screenRight: "media",
                }}
              />
            )}
            {vertical && (
              <div
                id=""
                {...{
                  style: {
                    gridArea: "-3/1/-1/2",
                    display: "grid",
                    placeSelf: "center start",
                    width: "70%",
                    gap: h(1),
                    zIndex: 9999,
                  },
                }}
              >
                <LeftArrow
                  {...{
                    onCurtainStart,
                    screenLeft: "product",
                    screenRight: "media",
                    containerStyle: {
                      width: "87.5%", // matain a 7/8th ratio
                      placeSelf: "start",
                    },
                  }}
                />
                <RightArrow
                  {...{
                    onCurtainStart,
                    screenLeft: "product",
                    screenRight: "media",
                  }}
                />
              </div>
            )}

            <div
              id="animated-svg"
              {...{
                style: {
                  gridArea: horizontal ? "-3/6/-1/8" : "4/1/4/3",
                  color: colors.pink,
                  fontSize: h(1),
                  letterSpacing: w(1),
                  placeSelf: horizontal ? "end" : "start",
                  zIndex: 999,
                  display: "grid",
                  gridTemplate: `
                    "a"
                    "b"
                  `,
                  gap: h(2),
                },
              }}
            >
              <SVGCircle
                {...{
                  active: true,
                  appearSlideUp: false,
                  style: {
                    margin: "auto",
                  },
                  containerStyle: {
                    placeSelf: horizontal ? "end" : "start",
                    padding: 0,
                  },
                  svgComponent: (
                    <AnimatedPerf
                      {...{ active: false, slideComponentUp: false }}
                    />
                  ),
                }}
              />
              <div
                id=""
                className=""
                {...{ style: { placeSelf: "end", marginRight: `-${w(1)}` } }}
              >
                IOCO PERFORMANCE
              </div>
            </div>

            <PageNumber
              {...{
                gridArea: horizontal ? "5/7/7/8" : "2/1/3/4",
                pageNumber: "03",
              }}
            />
            {vertical && (
              <RectangleButton
                {...{
                  gridArea: horizontal ? "9/2/9/4" : "-2/2/-1/-1",
                  text: "PREORDER NOW",
                  width: horizontal ? "75%" : "100%",
                  screen: "intro",
                  curtainTo: screens.menu,
                  onCurtainStart,
                }}
              />
            )}
          </div>

          {/* second grid with content */}
          <div
            id=""
            className=""
            {...{
              style: {
                gridArea: "AppContentContainer",
                display: "grid",
                gridTemplate: `
                  "a" ${horizontal ? 7 : 5}fr
                  "b" 1fr
                  "c" 1fr
                  "d" 1fr
                  "e" 1fr
                  "f" 1fr
                `,
              },
            }}
          >
            <BetterThanQuantum />
            <IOCO />
            <Biocomputer />
            {horizontal && (
              <RectangleButton
                {...{
                  gridArea: "e",
                  style: {
                    placeSelf: "center",
                  },
                  width: horizontal ? w(25) : w(70),
                  text: "PREORDER NOW",
                  onCurtainStart,
                  curtainTo: screens.preorder,
                }}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};
